// ============================================================================
// MarketContactCard — контакты клиента: имя, телефон (+ подтверждение),
// email, город и район. Телефон/город — профиль пользователя (EntityService),
// район — user_settings (отдельного реквизита в поставке нет).
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, HelpCircle, Save, Smartphone, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HelpHint } from '@/components/ui/HelpHint';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  saveMarketClientContacts,
  sendMarketPhoneCode,
  verifyMarketPhoneCode,
  type MarketClientProfile,
  type MarketContactInput,
} from '@/lib/marketProfile';

interface MarketContactCardProps {
  profile: MarketClientProfile;
  onSaved: (next: MarketClientProfile) => void;
  onReload: () => void;
}

function phoneDigits(value: string): number {
  return value.replace(/\D/g, '').length;
}

function statusLabel(profile: MarketClientProfile, phone: string): {
  text: string;
  tone: 'success' | 'warning' | 'muted' | 'danger';
} {
  if (!phone.trim()) return { text: 'Телефон не указан', tone: 'muted' };
  if (phone.trim() !== profile.phone.trim()) return { text: 'Не сохранён', tone: 'warning' };
  if (profile.phoneStatus === 'verified') return { text: 'Канал подтверждён', tone: 'success' };
  if (profile.phoneStatus === 'unknown') return { text: 'Статус канала не удалось проверить', tone: 'danger' };
  return { text: 'Канал не подтверждён', tone: 'warning' };
}

const TONE_CLASS: Record<string, string> = {
  success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700',
  warning: 'border-amber-500/30 bg-amber-500/10 text-amber-800',
  muted: 'border-border bg-muted text-muted-foreground',
  danger: 'border-destructive/30 bg-destructive/5 text-destructive',
};

export function MarketContactCard({ profile, onSaved, onReload }: MarketContactCardProps) {
  const [form, setForm] = useState<MarketContactInput>({
    name: profile.name,
    email: profile.email,
    phone: profile.phone,
    city: profile.city,
    district: profile.district,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [phoneMessage, setPhoneMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Профиль обновился (сохранение/проверка кода) — подставляем новые данные,
  // но НЕ затираем несохранённый ввод пользователя.
  useEffect(() => {
    if (dirty) return;
    setForm({
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      city: profile.city,
      district: profile.district,
    });
  }, [dirty, profile.city, profile.district, profile.email, profile.name, profile.phone]);

  const update = (key: keyof MarketContactInput, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setSaved(false);
    setSaveError('');
    setPhoneMessage('');
    setPhoneError('');
    if (key === 'phone') {
      setCodeSent(false);
      setCode('');
    }
  };

  const status = statusLabel(profile, form.phone);
  const phoneUnsaved = form.phone.trim() !== profile.phone.trim();
  const canVerify = !phoneUnsaved
    && form.phone.trim().length > 0
    && profile.phoneStatus !== 'verified'
    && !phoneBusy;

  const save = async () => {
    setSaveError('');
    setSaved(false);
    if (!form.name.trim()) {
      setSaveError('Укажите имя');
      return;
    }
    if (form.phone.trim() && phoneDigits(form.phone) < 10) {
      setSaveError('Укажите телефон для связи (не менее 10 цифр)');
      return;
    }
    setSaving(true);
    try {
      const next = await saveMarketClientContacts(profile.userId, profile, form);
      setDirty(false);
      setSaved(true);
      setPhoneMessage('');
      setPhoneError('');
      onSaved(next);
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: сохранение контактов');
      setSaveError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendCode = async () => {
    setPhoneError('');
    setPhoneMessage('');
    setPhoneBusy(true);
    try {
      await sendMarketPhoneCode(profile.userId, profile.phone);
      setCodeSent(true);
      setPhoneMessage('Код отправлен. Введите его ниже.');
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: отправка кода телефона');
      setPhoneError(friendlyError(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const verifyCode = async () => {
    setPhoneError('');
    setPhoneMessage('');
    setPhoneBusy(true);
    try {
      await verifyMarketPhoneCode(profile.userId, profile.phone, code);
      setCodeSent(false);
      setCode('');
      setPhoneMessage('Телефон подтверждён.');
      onReload();
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: проверка кода телефона');
      setPhoneError(friendlyError(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const districtHelp = useMemo(
    () => 'Отдельного поля «район» в заявке нет: район сохраняется в профиле и подставляется в адрес при создании заявки.',
    [],
  );

  return (
    <section className="space-y-4 rounded-2xl border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold">Контакты</h2>
        <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
          Эти данные видит мастер, который возьмёт вашу заявку.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="market-profile-name">Имя</Label>
        <Input
          id="market-profile-name"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          autoComplete="name"
          className="h-11 rounded-xl text-base"
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="market-profile-phone">Телефон</Label>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${TONE_CLASS[status.tone]}`}>
            {status.tone === 'success' ? <CheckCircle2 className="h-3 w-3" /> : null}
            {status.tone === 'warning' ? <XCircle className="h-3 w-3" /> : null}
            {status.text}
          </span>
        </div>
        <Input
          id="market-profile-phone"
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          type="tel"
          inputMode="tel"
          placeholder="+7 900 000-00-00"
          autoComplete="tel"
          className="h-11 rounded-xl text-base"
        />
        {profile.phoneStatus === 'verified' && profile.phoneVerifiedAt && !phoneUnsaved && (
          <div className="text-[10px] text-muted-foreground">
            Канал подтверждён {new Date(profile.phoneVerifiedAt).toLocaleDateString('ru-RU')}
          </div>
        )}
        {profile.phoneStatus === 'unknown' && (
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-destructive">
            <span>{profile.channelsFailure || 'Не удалось проверить статус телефона.'}</span>
            <button type="button" className="underline" onClick={onReload}>Повторить</button>
          </div>
        )}
        {phoneUnsaved && form.phone.trim() && (
          <div className="text-[10px] leading-4 text-amber-700">
            Сначала сохраните телефон — подтверждение относится к сохранённому номеру.
          </div>
        )}
        {!phoneUnsaved && profile.phoneStatus !== 'verified' && form.phone.trim() && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-[11px]"
            onClick={() => void sendCode()}
            disabled={!canVerify}
          >
            <Smartphone className="mr-1.5 h-3.5 w-3.5" />
            {phoneBusy ? 'Отправляем…' : codeSent ? 'Отправить код снова' : 'Подтвердить канал по коду'}
          </Button>
        )}
        {codeSent && (
          <div className="space-y-2 rounded-xl border bg-background/70 p-3">
            <div className="text-[11px] text-muted-foreground">Код из сообщения</div>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="h-10 rounded-lg text-base tracking-widest"
              />
              <Button
                type="button"
                className="h-10 shrink-0 rounded-lg"
                onClick={() => void verifyCode()}
                disabled={phoneBusy || code.trim().length < 4}
              >
                Проверить
              </Button>
            </div>
          </div>
        )}
        {phoneMessage && <div className="text-[11px] text-emerald-700">{phoneMessage}</div>}
        {phoneError && <div className="text-[11px] text-destructive">{phoneError}</div>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="market-profile-email">Электронная почта</Label>
        <Input
          id="market-profile-email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          type="email"
          autoComplete="email"
          placeholder="не указана"
          className="h-11 rounded-xl text-base"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="market-profile-city">Город</Label>
          <Input
            id="market-profile-city"
            value={form.city}
            onChange={(e) => update('city', e.target.value)}
            autoComplete="address-level2"
            placeholder="не указан"
            className="h-11 rounded-xl text-base"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="market-profile-district">Район</Label>
            <HelpHint text={districtHelp} />
          </div>
          <Input
            id="market-profile-district"
            value={form.district}
            onChange={(e) => update('district', e.target.value)}
            placeholder="не указан"
            className="h-11 rounded-xl text-base"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          className="h-11 flex-1 rounded-xl"
          onClick={() => void save()}
          disabled={saving || !dirty}
        >
          <Save className="mr-1.5 h-4 w-4" />
          {saving ? 'Сохраняем…' : 'Сохранить контакты'}
        </Button>
      </div>
      {saved && !dirty && (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Контакты сохранены
        </div>
      )}
      {saveError && <div className="text-[11px] text-destructive">{saveError}</div>}
      {!dirty && !saved && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <HelpCircle className="h-3 w-3" />
          Район хранится в настройках профиля и подставляется в адрес заявки.
        </div>
      )}
    </section>
  );
}
